import type { ConexaoDoKommo, RecorteDoProjeto as Recorte } from "@base/contracts";
import { Button, CampoDeSelecao, CampoDeTexto } from "@base/ui";
import { CheckCircle2, LoaderCircle, Plus, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { useDefinirRecorte } from "../application/conexoes.contexto";

type Linha = { readonly campoId: string; readonly valor: string };
type Grupo = { readonly operador: "e" | "ou"; readonly linhas: Linha[] };
type Rascunho = { readonly operador: "e" | "ou"; readonly grupos: Grupo[] };

const linhaVazia = (): Linha => ({ campoId: "", valor: "" });
const grupoVazio = (): Grupo => ({ operador: "e", linhas: [linhaVazia()] });
const rascunhoVazio = (): Rascunho => ({ operador: "e", grupos: [grupoVazio()] });

function rascunhoDe(recorte: Recorte | null): Rascunho {
  if (!recorte) return rascunhoVazio();
  return {
    operador: recorte.operador,
    grupos: recorte.grupos.map((grupo) => ({
      operador: grupo.operador,
      linhas: grupo.condicoes.map((condicao) => ({ campoId: String(condicao.campoId), valor: condicao.valor })),
    })),
  };
}

function recorteDe(rascunho: Rascunho): Recorte | null {
  const grupos = rascunho.grupos
    .map((grupo) => ({
      operador: grupo.operador,
      condicoes: grupo.linhas
        .filter((linha) => linha.campoId && linha.valor.trim())
        .map((linha) => ({ campoId: Number(linha.campoId), valor: linha.valor.trim() })),
    }))
    .filter((grupo) => grupo.condicoes.length > 0);
  return grupos.length === 0 ? null : { operador: rascunho.operador, grupos };
}

const OPCOES_DO_OPERADOR = [
  { valor: "e", rotulo: "E" },
  { valor: "ou", rotulo: "OU" },
] as const;

/**
 * Recorte fixo do projeto: grupos de "campo é valor", cada grupo com E/OU
 * entre as condições, e E/OU entre os grupos.
 */
export function RecorteDoProjeto({
  clienteId,
  conexao,
}: {
  readonly clienteId: string;
  readonly conexao: ConexaoDoKommo;
}) {
  const [rascunho, setRascunho] = useState<Rascunho>(() => rascunhoDe(conexao.recorte));
  const salvar = useDefinirRecorte(clienteId);
  const recorte = recorteDe(rascunho);
  const alterado = JSON.stringify(recorte) !== JSON.stringify(conexao.recorte);
  const campos = conexao.camposPersonalizados
    .filter((item) => item.tipo !== "numeric" && item.tipo !== "price" && item.tipo !== "monetary")
    .map((item) => ({ valor: String(item.id), rotulo: item.nome }));

  function mudar(proximo: Rascunho) {
    salvar.reset();
    setRascunho(proximo);
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate(recorte);
  }

  return (
    <section aria-labelledby="titulo-recorte" className="flex flex-col gap-bloco">
      <div className="flex flex-col gap-1">
        <h2 id="titulo-recorte" className="text-xl font-normal tracking-tight">
          Recorte deste projeto
        </h2>
        <p className="text-sm text-muted-foreground">
          Campo é tal E/OU campo é tal, em grupos. Sem linhas preenchidas, entram todos os leads.
        </p>
      </div>

      <form onSubmit={enviar} className="flex flex-col gap-4 rounded-3xl bg-card p-6 shadow-sm ring-1 ring-border/70">
        {rascunho.grupos.map((grupo, indiceDoGrupo) => (
          <div key={indiceDoGrupo} className="flex flex-col gap-3">
            {indiceDoGrupo > 0 ? (
              <CampoDeSelecao
                rotulo="Entre os grupos"
                value={rascunho.operador}
                onChange={(evento) => mudar({ ...rascunho, operador: evento.target.value as "e" | "ou" })}
                opcoes={[...OPCOES_DO_OPERADOR]}
                className="max-w-40 self-center"
              />
            ) : null}

            <div className="flex flex-col gap-3 rounded-2xl border border-border p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Grupo {indiceDoGrupo + 1}</p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`Remover grupo ${indiceDoGrupo + 1}`}
                  onClick={() => mudar(semGrupo(rascunho, indiceDoGrupo))}
                >
                  <Trash2 aria-hidden="true" />
                  Excluir
                </Button>
              </div>

              {grupo.linhas.map((linha, indice) => {
                const campo = conexao.camposPersonalizados.find((item) => String(item.id) === linha.campoId);
                return (
                  <div key={indice} className="flex flex-col gap-3">
                    {indice > 0 ? (
                      <CampoDeSelecao
                        rotulo="Entre as condições"
                        value={grupo.operador}
                        onChange={(evento) =>
                          mudar({
                            ...rascunho,
                            grupos: rascunho.grupos.map((item, i) =>
                              i === indiceDoGrupo ? { ...item, operador: evento.target.value as "e" | "ou" } : item,
                            ),
                          })
                        }
                        opcoes={[...OPCOES_DO_OPERADOR]}
                        className="max-w-40"
                      />
                    ) : null}
                    <div className="flex flex-col gap-3 md:flex-row md:items-end">
                      <CampoDeSelecao
                        rotulo="Campo"
                        textoVazio="Escolha o campo"
                        value={linha.campoId}
                        onChange={(evento) =>
                          atualizarLinha(rascunho, indiceDoGrupo, indice, { campoId: evento.target.value, valor: "" }, mudar)
                        }
                        opcoes={campos}
                        className="md:flex-1"
                      />
                      <p className="hidden pb-3 text-sm text-muted-foreground md:block" aria-hidden="true">
                        é
                      </p>
                      {campo ? (
                        campo.opcoes.length > 0 ? (
                          <CampoDeSelecao
                            rotulo="Valor"
                            textoVazio="Escolha o valor"
                            value={linha.valor}
                            onChange={(evento) =>
                              atualizarLinha(rascunho, indiceDoGrupo, indice, { valor: evento.target.value }, mudar)
                            }
                            opcoes={campo.opcoes.map((opcao) => ({ valor: opcao, rotulo: opcao }))}
                            className="md:flex-1"
                          />
                        ) : (
                          <CampoDeTexto
                            rotulo="Valor"
                            value={linha.valor}
                            onChange={(evento) =>
                              atualizarLinha(rascunho, indiceDoGrupo, indice, { valor: evento.target.value }, mudar)
                            }
                            className="md:flex-1"
                          />
                        )
                      ) : (
                        <CampoDeSelecao
                          rotulo="Valor"
                          textoVazio="Escolha o campo antes"
                          value=""
                          disabled
                          opcoes={[]}
                          className="md:flex-1"
                        />
                      )}
                      {grupo.linhas.length > 1 ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={`Remover condição ${indice + 1} do grupo ${indiceDoGrupo + 1}`}
                          onClick={() =>
                            mudar({
                              ...rascunho,
                              grupos: rascunho.grupos.map((item, i) =>
                                i === indiceDoGrupo
                                  ? { ...item, linhas: item.linhas.filter((_, j) => j !== indice) }
                                  : item,
                              ),
                            })
                          }
                        >
                          <Trash2 aria-hidden="true" />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                );
              })}

              <Button
                type="button"
                variant="outline"
                disabled={grupo.linhas.length >= 10}
                onClick={() =>
                  mudar({
                    ...rascunho,
                    grupos: rascunho.grupos.map((item, i) =>
                      i === indiceDoGrupo ? { ...item, linhas: [...item.linhas, linhaVazia()] } : item,
                    ),
                  })
                }
              >
                <Plus aria-hidden="true" />
                Adicionar condição
              </Button>
            </div>
          </div>
        ))}

        <Button
          type="button"
          variant="outline"
          disabled={rascunho.grupos.length >= 10}
          onClick={() => mudar({ ...rascunho, grupos: [...rascunho.grupos, grupoVazio()] })}
        >
          <Plus aria-hidden="true" />
          Adicionar grupo
        </Button>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <p
            role={salvar.isError ? "alert" : "status"}
            className={salvar.isError ? "text-sm text-destructive" : "text-sm text-muted-foreground"}
          >
            {salvar.isError ? mensagemDoErro(salvar.error) : null}
            {salvar.isSuccess ? (
              <span className="inline-flex items-center gap-2 text-success">
                <CheckCircle2 className="size-4" aria-hidden="true" />
                Recorte salvo.
              </span>
            ) : null}
          </p>
          <Button type="submit" disabled={!alterado || salvar.isPending}>
            {salvar.isPending ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
            Salvar recorte
          </Button>
        </div>
      </form>
    </section>
  );
}

function semGrupo(rascunho: Rascunho, indice: number): Rascunho {
  const grupos = rascunho.grupos.filter((_, i) => i !== indice);
  return grupos.length === 0 ? rascunhoVazio() : { ...rascunho, grupos };
}

function atualizarLinha(
  rascunho: Rascunho,
  indiceDoGrupo: number,
  indice: number,
  pedaco: Partial<Linha>,
  mudar: (proximo: Rascunho) => void,
) {
  mudar({
    ...rascunho,
    grupos: rascunho.grupos.map((grupo, i) =>
      i === indiceDoGrupo
        ? { ...grupo, linhas: grupo.linhas.map((linha, j) => (j === indice ? { ...linha, ...pedaco } : linha)) }
        : grupo,
    ),
  });
}
